import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router';
import useDebounce from './useDebounce';
import { useFetchReducer } from '../../../../../../../hooks/reducers/useFetchReducer';

const ErrorType = {
  NO_DATA: 'noData',
  INVALID_SEARCH_TERM: 'invalidSearchTerm',
} as const;
type ErrorType = (typeof ErrorType)[keyof typeof ErrorType];

export type ComboBoxInputProps = {
  suggestUrl: string;
  inputValue?: string;
  threshold?: number;
  thresholdSingleCharacterExceptions?: string[];
  // Event Handler
  onInput?: React.ChangeEventHandler<HTMLInputElement>;
};

const isValidInput = (
  value: string | undefined,
  threshold: number,
  thresholdSingleCharacterExceptions: string[],
): value is string => {
  return value !== undefined && (value.length >= threshold || thresholdSingleCharacterExceptions.includes(value));
};

export default <TResponse>({
  suggestUrl,
  inputValue: defaultInputValue,
  threshold = 2,
  thresholdSingleCharacterExceptions = [],
  onInput: onInputEventHandler,
}: ComboBoxInputProps) => {
  const [inputValue, setInputValue] = useState(defaultInputValue);
  const [query, setQuery] = useState<string>();
  const [error, setError] = useState<ErrorType>();
  const normalizedQueryValue = query?.trim();
  const debouncedQueryValue = useDebounce(normalizedQueryValue, 300);
  const searchHistory = useRef(new Map<string, TResponse>());
  const formInputRef = useRef<HTMLInputElement>(null);

  const { key } = useLocation();

  /**
   * Clearing the Combobox Input by removing input + query and clearing all saved results.
   */
  const clearInput = useCallback(() => {
    searchHistory.current = new Map<string, TResponse>();
    setInputValue(undefined);
    setQuery(undefined);
  }, []);

  const resetError = useCallback(() => setError(undefined), []);

  const { response, requestError, loading, startRequest, receivedResponse, receivedError } = useFetchReducer<
    TResponse | undefined,
    ErrorType
  >();

  const getResult = useCallback(
    async (q: string) => {
      const cachedResponse = searchHistory.current.get(q);
      if (cachedResponse) {
        receivedResponse(cachedResponse);
      } else {
        startRequest();
        try {
          const axiosResponse = await axios.get<TResponse>(suggestUrl, { params: { q } });
          searchHistory.current.set(q, axiosResponse.data);
          receivedResponse(axiosResponse.data);
        } catch {
          receivedError(ErrorType.NO_DATA);
        }
      }
    },
    [receivedError, receivedResponse, startRequest, suggestUrl],
  );

  useEffect(() => {
    if (isValidInput(debouncedQueryValue.value, threshold, thresholdSingleCharacterExceptions)) {
      getResult(debouncedQueryValue.value).catch();
    } else {
      // setError(ErrorType.INVALID_SEARCH_TERM);
      // setInputValue(undefined);
    }
    // thresholdSingleCharacterExceptions should not be in deps!
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQueryValue, getResult, threshold]);
  useEffect(() => {
    if (requestError) {
      setError(requestError);
    }
  }, [requestError]);

  // empty cached results if
  // -> switch page, e.g. by navigation trough page. using location key
  // -> suggestUrl changed, see linkChooser
  useEffect(() => {
    clearInput();
  }, [suggestUrl, key, clearInput]);

  /**
   * Changing characters on user input
   * deselecting current selection from listbox
   * resetting hidden input
   * @param event {event} main input onchange event of recipientId-combobox
   */
  const onInput = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (onInputEventHandler) {
        onInputEventHandler(event);
      }
      setInputValue(event.target.value);
      setQuery(event.target.value);
      resetError();
    },
    [onInputEventHandler, resetError],
  );

  const isValidInputValue = isValidInput(inputValue, threshold, thresholdSingleCharacterExceptions);
  const isValidDebouncedQueryValue = isValidInput(
    debouncedQueryValue.value,
    threshold,
    thresholdSingleCharacterExceptions,
  );

  // reset response when input or debounced input are not valid:
  useEffect(() => {
    if ((isValidInputValue && isValidDebouncedQueryValue) === false) {
      receivedResponse(undefined);
    }
  }, [isValidInputValue, isValidDebouncedQueryValue, receivedResponse]);

  return {
    error,
    loading,
    response,
    inputValue,
    isValidInputValue,
    formInputRef,
    onInput,
    clearInput,
    resetError,
  };
};
